import { useEffect, useMemo } from "react";

import { useLocation } from "@/lib/router";
import {
  resolveContentRegion,
  resolveContentRegionFromCurrency,
} from "@/utils/resolveContentRegion";

/** Which API answer the page trusts for the region. */
export const LOCALISED_PRICING_SOURCE = {
  CARDS: "cards",
  METADATA: "metadata",
} as const;

export type TLocalisedPricingSource =
  (typeof LOCALISED_PRICING_SOURCE)[keyof typeof LOCALISED_PRICING_SOURCE];

type TUseLocalisedPricingArgs = {
  source: TLocalisedPricingSource;
  /** `countryCode` off the `/pricing/cards` response. */
  cardsCountryCode?: string;
  /** `planCards[0].pricing.currency` — symbol or ISO code, depending on the API. */
  planCurrency?: string;
  /** `country_code` off `/user` — the country the user is currently on. */
  userCountryCode?: string;
};

// Resolves whether the page is showing localised pricing, and logs all three
// signals side by side while we confirm the APIs agree.
export function useLocalisedPricing({
  source,
  cardsCountryCode,
  planCurrency,
  userCountryCode,
}: TUseLocalisedPricingArgs) {
  const { pathname, search } = useLocation();

  const byUserCountry = resolveContentRegion(userCountryCode);
  const byCardsCountry = resolveContentRegion(cardsCountryCode);
  const byPlanCurrency = resolveContentRegionFromCurrency(planCurrency);

  // Memoised so callers can put it straight in a dependency array.
  const resolved = useMemo(
    () => ({ byCardsCountry, byPlanCurrency, byUserCountry }),
    [byCardsCountry, byPlanCurrency, byUserCountry],
  );

  const byRouteSignal =
    source === LOCALISED_PRICING_SOURCE.CARDS ? byCardsCountry : byPlanCurrency;

  // `/user` first, then the route's own signal, then the currency it implies.
  const region = byUserCountry ?? byRouteSignal ?? byPlanCurrency;

  const isLocalisedPricing = !!region;

  // `||` not `??` — the pricing APIs return "" rather than omitting the field.
  const resolvedCountry = (userCountryCode || cardsCountryCode)?.toUpperCase();

  return { isLocalisedPricing, region, resolvedCountry, resolved };
}
